import React, { useMemo, useState } from 'react';
import { Delivery } from '../types';

interface Props {
  deliveries: Delivery[];
  /** Ključevi izabranih isporuka, bez prefiksa. */
  selected: string[];
  onToggle: (documentKey: string) => void;
}

/**
 * B2C isporuke koje čekaju razvoz.
 *
 * Prikazuju se i one koje se ne mogu planirati, sa razlogom. Da se sakriju,
 * operater ne bi znao da mu red za potvrdu adresa zadržava isporuke — video bi
 * kraću listu i mislio da nema posla.
 */
export default function DeliveryPanel({ deliveries, selected, onToggle }: Props) {
  const [showBlocked, setShowBlocked] = useState(false);

  const { ready, blocked } = useMemo(
    () => ({
      ready: deliveries.filter((d) => d.canBePlanned),
      blocked: deliveries.filter((d) => !d.canBePlanned)
    }),
    [deliveries]
  );

  // Razlozi se broje, jer operatera zanima gde je usko grlo, a ne spisak od
  // četrdeset redova sa istom rečenicom.
  const obstacles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const delivery of blocked) {
      const reason = delivery.obstacle ?? 'nepoznato';
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [blocked]);

  if (deliveries.length === 0) {
    return <p className="empty">Nema B2C isporuka koje čekaju.</p>;
  }

  return (
    <>
      {ready.length === 0 ? (
        <p className="empty">Nijedna isporuka nije spremna za turu.</p>
      ) : (
        <ul className="deliveries">
          {ready.map((delivery) => (
            <li key={delivery.documentKey}>
              <button
                type="button"
                className={
                  'delivery'
                  + (selected.includes(delivery.documentKey) ? ' delivery--selected' : '')
                  + (delivery.isOverdue ? ' delivery--overdue' : '')
                }
                onClick={() => onToggle(delivery.documentKey)}
              >
                <span className="delivery__address">
                  {delivery.address}, {delivery.city}
                </span>
                <span className="delivery__age">
                  {delivery.waitingDays} {delivery.waitingDays === 1 ? 'dan' : 'dana'}
                </span>
                <span className="delivery__meta">
                  {delivery.customer || 'bez imena'} · {delivery.pieces} kom
                  {delivery.requiresTwoPeople && <strong> · dva čoveka</strong>}
                  {delivery.floor !== null && delivery.floor > 0 && (
                    <> · {delivery.floor}. sprat{delivery.hasLift ? ', lift' : ', bez lifta'}</>
                  )}
                  {/* Ko je prodao. Ako nije centralni magacin, roba se možda
                      diže u tom objektu — aplikacija to ne zna, operater zna. */}
                  {delivery.originName && <> · prodao {delivery.originName}</>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {blocked.length > 0 && (
        <div className="blockedlist">
          <button
            type="button"
            className="disc__head"
            aria-expanded={showBlocked}
            onClick={() => setShowBlocked((v) => !v)}
          >
            <span className="disc__caret" aria-hidden="true">›</span>
            <span className="disc__title">{blocked.length} isporuka ne može na turu</span>
          </button>

          <ul className="blockedlist__reasons">
            {obstacles.map(([reason, count]) => (
              <li key={reason}>
                <span className="blockedlist__count">{count}</span> {reason}
              </li>
            ))}
          </ul>

          {showBlocked && (
            <ul className="rows disc__body">
              {blocked.map((delivery) => (
                <li key={delivery.documentKey}>
                  <span className="row__name">
                    {delivery.address || '(bez ulice)'}, {delivery.city || '(bez mesta)'}
                  </span>
                  <span className="row__meta">{delivery.obstacle}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
